(function () {
  'use strict';
  // Picks are kept per customer, not per browser: identity is proven with a
  // token Shopify's own Liquid engine signs server-side (see theme.liquid),
  // matching the same scheme the system builder's own pick history uses (see
  // history.js). There is no anonymous fallback — recording and reviewing
  // both need a login, which is the point: "your" picks only mean something
  // once they are actually tied to your account.
  //
  // Synced to the equipplus-picks Worker so a pick made on one device shows
  // up on another. localStorage is kept as a same-device fallback only — the
  // server answer always wins once it arrives, and render() re-paints in
  // place when that happens.
  //
  // Review happens on its own page (/pages/my-picks), not a modal here — that
  // page just needs an #AffiliateHistoryList element and render() below fills
  // it in on load, the same way it would anywhere else this script runs.
  var LIMIT = 50;
  var MY_PICKS_URL = '/pages/my-picks';
  var cache = null;       // null until the first read() call primes it
  var loading = null;

  function customerId() { return window.SQS_CUSTOMER_ID || null; }
  function loginUrl() { return window.SQS_LOGIN_URL || '/account/login'; }
  function apiReady() { return !!(window.SQS_PICKS_API && window.SQS_CUSTOMER_TOKEN); }
  function apiUrl(params) {
    var u = new URL(window.SQS_PICKS_API + '/picks');
    Object.keys(params).forEach(function (k) { if (params[k] != null) u.searchParams.set(k, params[k]); });
    return u.toString();
  }
  function key() {
    var shop = window.SQS_SHOP_DOMAIN || 'sqs';
    return 'equipplusPickHistory:' + shop + ':' + customerId();
  }
  function localRead() {
    try { var v = JSON.parse(localStorage.getItem(key()) || '[]'); return Array.isArray(v) ? v : []; }
    catch (e) { return []; }
  }
  function localWrite(items) {
    try { localStorage.setItem(key(), JSON.stringify(items.slice(0, LIMIT))); } catch (e) {}
  }
  function clean(v) { return String(v || '').replace(/\s+/g, ' ').trim(); }

  // Every call to the picks Worker is best-effort — a failure falls back to
  // this browser's own localStorage rather than breaking the page. That is
  // the right behaviour and it stays. What was wrong was doing it in total
  // silence: the Worker's allowed-origin list did not include the store's
  // real domain, so EVERY call failed, saved picks quietly stopped syncing
  // and started living in one browser, and nothing anywhere said so — not
  // a console line, not a UI hint. Months of "why did my deleted pick come
  // back" traced to a mismatch that one warning would have surfaced.
  function warnApi(what, err) {
    try {
      console.warn('[equipplus picks] could not ' + what + ' — saved picks are ' +
        'falling back to this browser only, and will not sync across devices. ' +
        'If this persists, check the picks Worker\'s allowed origins against ' +
        location.origin + '.', err);
    } catch (e) {}
  }

  // Fetches once per page load, then read() serves from memory. If the
  // server answers with real data, that replaces the local guess and the
  // list re-paints — the common case being a device that never saved
  // anything locally but has real history from elsewhere.
  function ensureLoaded() {
    if (cache) return loading || Promise.resolve(cache);
    if (loading) return loading;
    cache = localRead();
    if (!customerId() || !apiReady()) return Promise.resolve(cache);
    loading = fetch(apiUrl({ customer: customerId(), token: window.SQS_CUSTOMER_TOKEN, kind: 'picks' }))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (d && Array.isArray(d.items)) {
          var gone = deletedIds('picks');
          cache = gone.length ? d.items.filter(function (it) { return gone.indexOf(it.id) === -1; }) : d.items;
          localWrite(cache);
          render();
        }
        return cache;
      })
      .catch(function (e) { warnApi('load your saved picks', e); return cache; })
      .finally(function () { loading = null; });
    return loading;
  }

  function read() {
    if (!customerId()) return [];
    ensureLoaded();
    return ensureIds(cache || localRead());
  }
  // "Clear history" clears what this page actually shows — product picks
  // AND saved systems. Clearing only half of a list the customer sees as
  // one list is what "I cleared it and they're still there" looked like.
  function write(items) {
    var keep = items.map(function (it) { return it.id; });
    (cache || []).forEach(function (it) { if (keep.indexOf(it.id) === -1) markDeleted('picks', it.id); });
    if (!items.length) sqsRead().forEach(function (it) { markDeleted('sqs', it.id); });
    cache = items;
    localWrite(items);
    if (apiReady()) {
      fetch(apiUrl({ customer: customerId(), token: window.SQS_CUSTOMER_TOKEN, kind: 'picks' }), { method: 'DELETE' })
        .catch(function (e) { warnApi('clear your saved picks', e); });
    }
    if (!items.length) {
      sqsLocalWrite([]);
      if (apiReady()) {
        fetch(apiUrl({ customer: customerId(), token: window.SQS_CUSTOMER_TOKEN, kind: 'sqs' }), { method: 'DELETE' })
          .catch(function (e) { warnApi('clear your saved systems', e); });
      }
    }
  }

  function newId() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 8); }

  /* ------------------------------------------------------------------
     System-builder designs (kind "sqs") are NOT copied into this store.
     They are read straight from the one place they actually live — the
     same localStorage key history.js writes, and the same Worker records
     under kind "sqs".

     They used to be mirrored in here, and keeping two copies of one pick
     in step turned out to be unwinnable: the mirror could only ever diff
     on presence, so it could not tell "not copied across yet" from
     "deleted on purpose", and deleting on one side left the other side
     holding a copy nothing could reach. Three rounds of tombstones and
     reconciliation sweeps each fixed one path through that and left
     another open. One list, one delete, no reconciliation.
     ------------------------------------------------------------------ */
  /* Ids deleted locally, so a fetch that resolves after a delete — or a
     DELETE the Worker never matched — cannot quietly put a pick back.
     Keyed by the pick's own id, not by a reconstructed URL: the id is
     stable, travels with the record, and means the same thing in every
     file that touches it. The "sqs" key here is byte-for-byte the one
     history.js reads (sqsHistoryDeletedKey there) — deleting a saved
     system on this page has to be honoured by the builder page too. */
  function deletedKey(kind) {
    var shop = window.SQS_SHOP_DOMAIN || 'sqs';
    return (kind === 'sqs' ? 'sqsHistoryDeleted:' : 'equipplusPickDeleted:') + shop + ':' + customerId();
  }
  function deletedIds(kind) {
    try { return JSON.parse(localStorage.getItem(deletedKey(kind)) || '[]'); } catch (e) { return []; }
  }
  function markDeleted(kind, id) {
    try {
      var k = deletedKey(kind);
      var list = JSON.parse(localStorage.getItem(k) || '[]');
      if (list.indexOf(id) === -1) { list.push(id); localStorage.setItem(k, JSON.stringify(list)); }
    } catch (e) {}
  }

  function sqsKey() { return 'sqsHistory:' + (window.SQS_SHOP_DOMAIN || 'sqs') + ':' + customerId(); }
  function sqsLocalRead() {
    try { var v = JSON.parse(localStorage.getItem(sqsKey()) || '[]'); return Array.isArray(v) ? v : []; }
    catch (e) { return []; }
  }
  function sqsLocalWrite(items) {
    try { localStorage.setItem(sqsKey(), JSON.stringify(items)); } catch (e) {}
  }

  /* Deliberately NOT cached in a variable here, unlike this module's own
     product picks: history.js writes this same key whenever the builder
     saves or deletes, and on the builder page both scripts are live at
     once. A second in-memory copy would go stale the moment the other
     module wrote — which is the same "two copies drifting" problem this
     whole change exists to remove, just one layer down. localStorage is
     the single in-browser source of truth; read it every time. It holds
     at most 40 small records, so this is not a cost worth optimising. */
  var sqsFetched = false;
  var sqsLoading = null;
  function sqsEnsureLoaded() {
    if (sqsFetched || sqsLoading) return sqsLoading || Promise.resolve();
    if (!customerId() || !apiReady()) { sqsFetched = true; return Promise.resolve(); }
    sqsLoading = fetch(apiUrl({ customer: customerId(), token: window.SQS_CUSTOMER_TOKEN, kind: 'sqs' }))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (d && Array.isArray(d.items)) {
          var gone = deletedIds('sqs');
          sqsLocalWrite(gone.length ? d.items.filter(function (it) { return gone.indexOf(it.id) === -1; }) : d.items);
          render();
        }
      })
      .catch(function (e) { warnApi('load your saved systems', e); })
      .finally(function () { sqsFetched = true; sqsLoading = null; });
    return sqsLoading;
  }
  function sqsRead() {
    if (!customerId()) return [];
    sqsEnsureLoaded();
    return sqsLocalRead();
  }
  function sqsRemove(id) {
    sqsLocalWrite(sqsLocalRead().filter(function (it) { return it.id !== id; }));
    markDeleted('sqs', id);
    if (apiReady()) {
      fetch(apiUrl({ customer: customerId(), token: window.SQS_CUSTOMER_TOKEN, kind: 'sqs', id: id }), { method: 'DELETE' })
        .catch(function (e) { warnApi('delete a saved system', e); });
    }
  }

  function sqsLabel(entry) {
    var archName = entry.arch === 'hybrid' ? 'Hybrid'
                 : entry.arch === 'generator' ? 'Solar generator' : '3-component';
    var bits = [archName];
    if (entry.region) bits.push(entry.region);
    if (entry.kwhDay) bits.push((+entry.kwhDay).toFixed(1) + ' kWh/day');
    return bits.join(' · ');
  }
  function fmtMoney(n, cur) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur || 'CAD', maximumFractionDigits: 0 }).format(n);
    } catch (e) { return (cur || 'CAD') + ' ' + Math.round(n || 0); }
  }
  // The builder's own path travels with the entry (history.js records it at
  // save time) rather than being reconstructed from whatever page happens
  // to be asking — this list renders on /pages/my-picks, which is not the
  // page the link has to point back to.
  function sqsAsItem(entry) {
    return {
      id: entry.id,
      isSystem: true,
      title: sqsLabel(entry) + ' — off-grid system design',
      url: (entry.path || '/pages/generate-slp') + '?' + entry.query + '&_at=' + encodeURIComponent(entry.savedAt),
      merchant: 'System builder',
      details: [fmtMoney(entry.total, entry.currency) + ' · ' + entry.parts + ' part' + (entry.parts === 1 ? '' : 's')],
      pickedAt: entry.savedAt
    };
  }

  // Deleting one entry needs something stable to match it by. Items never
  // had one — only "Clear history" (delete-everything) existed before this.
  // Assigned lazily here rather than only in record(), so a pick already in
  // storage before this shipped becomes deletable on its own the next time
  // it's read, not left permanently stuck with no way to remove just that
  // one.
  function ensureIds(items) {
    var changed = false;
    items.forEach(function (it) { if (!it.id) { it.id = newId(); changed = true; } });
    if (changed) localWrite(items);
    return items;
  }

  function record(d) {
    if (!customerId()) return null;   // signed out: nothing to attach this to
    var item = { id: newId(), title: clean(d.title) || 'Affiliate product', url: d.url || window.location.href, merchant: clean(d.merchant), source: clean(d.source) || 'storefront', quantity: Number(d.quantity) || 1, details: Array.isArray(d.details) ? d.details.map(clean).filter(Boolean) : [], pickedAt: new Date().toISOString() };
    var items = read(); items.unshift(item);
    cache = items;
    localWrite(items);
    if (apiReady()) {
      fetch(apiUrl({}), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer: customerId(), token: window.SQS_CUSTOMER_TOKEN, kind: 'picks', entry: item })
      }).catch(function () {});
    }
    document.dispatchEvent(new CustomEvent('equipplus:pick-recorded', { detail: item }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: 'affiliate_pick', affiliate_pick: item });
    return item;
  }

  // Removes just this one entry, the same shape as history.js's own
  // sqsHistoryStore.remove (kind "sqs" there, "picks" here — same Worker).
  // A pick from before ids existed only just got one in this browser via
  // ensureIds, so the server was never told that id and this DELETE won't
  // match anything there — it still comes off this device's own list
  // immediately, which is what "Delete" on this page has to do regardless;
  // full cross-device removal for those older entries follows once they've
  // synced an id everywhere, same as any other locally-generated field.
  // One delete control per row, whichever list that row came from — the
  // customer should not have to know there are two.
  function removeOne(id) {
    if (!customerId() || !id) return;
    var all = read();
    if (!all.some(function (it) { return it.id === id; })) { sqsRemove(id); return; }
    var items = all.filter(function (it) { return it.id !== id; });
    cache = items;
    localWrite(items);
    markDeleted('picks', id);
    if (apiReady()) {
      fetch(apiUrl({ customer: customerId(), token: window.SQS_CUSTOMER_TOKEN, kind: 'picks', id: id }), { method: 'DELETE' })
        .catch(function (e) { warnApi('delete that pick', e); });
    }
  }

  function esc(v) { return String(v || '').replace(/[&<>'"]/g, function (c) { return {'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[c]; }); }
  function linkFor(item) { return item.url || ('/search?q=' + encodeURIComponent(item.title || '') + '&type=product'); }

  // What this page shows: product picks and saved systems, newest first,
  // each read from the one store that owns it.
  function readAll() {
    if (!customerId()) return [];
    return read().concat(sqsRead().map(sqsAsItem))
      .sort(function (a, b) { return String(b.pickedAt || '').localeCompare(String(a.pickedAt || '')); });
  }

  function render() {
    var items = readAll(), list = document.getElementById('AffiliateHistoryList'), count = document.querySelector('[data-affiliate-picks-count]');
    if (count) { count.textContent = items.length ? String(items.length) : ''; count.hidden = !items.length; }
    if (!list) return;
    list.innerHTML = items.length ? items.map(function (item) {
      var url = linkFor(item);
      var action = '<a class="affiliate-history__link" href="' + esc(url) + '">Open</a>';
      var del = '<button type="button" class="affiliate-history__del" data-del-affiliate-pick="' + esc(item.id) + '" aria-label="Delete this saved pick">Delete</button>';
      var title = '<a class="affiliate-history__title" href="' + esc(url) + '"><strong>' + esc(item.title) + '</strong></a>';
      var details = Array.isArray(item.details) && item.details.length ? '<ul class="affiliate-history__details">' + item.details.map(function (detail) { return '<li>' + esc(detail) + '</li>'; }).join('') + '</ul>' : '';
      return '<li class="affiliate-history__item"><div>' + title + (item.merchant ? '<small>' + esc(item.merchant) + '</small>' : '') + details + '<time datetime="' + esc(item.pickedAt) + '">' + new Date(item.pickedAt).toLocaleString() + '</time></div><span class="affiliate-history__actions">' + action + del + '</span></li>';
    }).join('') : '<li class="affiliate-history__empty">Your product picks will appear here.</li>';
  }

  // Every entry point that either shows saved picks or records a new one
  // funnels through here, so a signed-out visitor always lands on the same
  // login prompt rather than a handful of differently-broken paths.
  function requireLogin() { window.location.href = loginUrl(); }

  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-affiliate-history-clear]')) { write([]); render(); return; }

    var del = event.target.closest('[data-del-affiliate-pick]');
    if (del) { removeOne(del.dataset.delAffiliatePick); render(); return; }

    var pick = event.target.closest('[data-affiliate-pick]'); if (!pick) return;
    // A real link ("View deal at partner") always goes through — the partner
    // visit is the point, and the affiliate revenue should not depend on
    // whether the visitor happens to be signed in. Only the save to history
    // is conditional on that. A bare button ("Save to my picks") has no
    // destination of its own, so its whole purpose is the save — a signed-out
    // click there goes to login instead of doing nothing.
    var isLink = pick.tagName === 'A' && pick.getAttribute('href');
    if (!isLink && !customerId()) { event.preventDefault(); requireLogin(); return; }

    var details = [];
    if (pick.dataset.affiliateDetails) {
      try { details = JSON.parse(decodeURIComponent(pick.dataset.affiliateDetails)); } catch (error) {}
    }
    record({ title: pick.dataset.affiliateTitle || pick.textContent, url: pick.dataset.affiliateUrl || pick.href || window.location.href, merchant: pick.dataset.affiliateMerchant || '', source: pick.dataset.affiliateSource || 'storefront', quantity: pick.dataset.affiliateQuantity || 1, details: details });
    if (!isLink) {
      pick.textContent = 'Saved ✓';
      setTimeout(function () { pick.textContent = 'Save to my picks'; }, 2000);
    }
  });

  // Belt and suspenders: if any block still posts straight to /cart (a
  // third-party app block, say), send the visitor to their picks page
  // instead of letting a real cart transaction happen.
  document.addEventListener('submit', function (event) {
    var action = event.target && event.target.action ? String(event.target.action) : '';
    if (/\/cart(?:\/add|\/change|\/update)?(?:[/?#]|$)/i.test(action)) {
      event.preventDefault();
      window.location.href = customerId() ? MY_PICKS_URL : loginUrl();
    }
  }, true);

  document.addEventListener('equipplus:pick-recorded', render);
  document.addEventListener('DOMContentLoaded', render, { once: true });
  window.EquipplusAffiliateHistory = { read: read, readAll: readAll, record: record, render: render, removeOne: removeOne };
}());
